import _ from 'lodash';
import { Coordinate } from 'ol/coordinate';
import { flatten } from 'lodash';
import { fromLatLon } from 'utm';
import { GridPatterns, Mission, Sample, SoilCore } from '../../db';
import { cleanSet, distanceLatLon } from '../../utils';
import { REORDER_TOLERANCE_METERS } from '../../constants';
import { setSampleAndCoreOrder } from './utils';
import logger from '../../logger';

class PathReorderer {
  private activeSampleId: string | undefined = undefined;
  private coresNumberToAddBySampleId: { [sample_id: string]: number } = {};

  constructor(
    private mission: Mission,
    private coords: Coordinate[],
    private renumber: boolean,
    private sampling: boolean,
  ) {}

  public async reorder() {
    const error_messages: string[] = [];
    const patternType = this.mission.getPrimarySpec()?.pattern_type;

    // grid sampling
    let re_ordered_samples: Sample[] = [];
    let re_ordered_cores: SoilCore[] = [];

    if (patternType !== GridPatterns.ZONE) {
      re_ordered_samples = await this.handleGridCase();
    }
    // zone sampling - use the same logic for regular zone and zone interleave
    else {
      re_ordered_cores = this.handleZoneCase();
    }

    await logger.log('PathReorderer - reorder - calculate_path_restore_cores');
    await this.mission.calculate_path_restore_cores(
      re_ordered_samples.length ? re_ordered_samples : re_ordered_cores,
      null,
      true, // For reordering - when core numbers change, we need to match by position instead of core numbers
    );

    return error_messages;
  }

  private handleZoneCase(): SoilCore[] {
    this.mission.deleteAllExtraCores();
    const cores = this.mission.getAllCoresSortedByWaypointNumber();

    if (cores.length < 2) {
      return cores;
    }

    const coresMap = new Map(cores.map((i) => [i, false]));
    this.countCoresToAdd(coresMap);

    const selectedCores: SoilCore[] = [];

    for (const [lat, lon] of this.coords) {
      const utmLoc = fromLatLon(lat, lon);
      const closestCore: SoilCore | null = this.findClosestCore(coresMap, utmLoc, this.isPointInNewZone(lat, lon));

      if (!closestCore || coresMap.get(closestCore)) {
        // If no closest core found or if it is already found, skip to next coordinate
        continue;
      }

      this.addCoreToNewCores(closestCore, selectedCores, coresMap);
    }

    const originalCoreIndices = cores.map((_: SoilCore, i: number) => i);

    const selectedCoreIndices: number[] = [];
    selectedCores.forEach((core: SoilCore) => {
      const index = cores.indexOf(core);

      if (index !== -1) {
        selectedCoreIndices.push(index);
      }
    });

    const newCoreIndices = this.reorderPathIndices(
      originalCoreIndices,
      selectedCoreIndices,
      originalCoreIndices.reduce((acc, coreIndex) => {
        const core = cores[coreIndex];
        acc[coreIndex] = { lat: core.lat, lon: core.lon };

        return acc;
      }, {}),
    );

    const reorderedCores: SoilCore[] = newCoreIndices.map((index: number) => cores[index]);

    if (!this.mission.is_zone_interleave_mission()) {
      setSampleAndCoreOrder(reorderedCores);
    }

    return reorderedCores;
  }

  private addCoreToNewCores(core: SoilCore, reorderedCores: SoilCore[], coresMap: Map<SoilCore, boolean>) {
    reorderedCores.push(core);
    coresMap.set(core, true);

    if (!this.activeSampleId) {
      this.activeSampleId = core.sample_id!;
    }

    this.coresNumberToAddBySampleId[this.activeSampleId] -= 1;

    if (this.coresNumberToAddBySampleId[this.activeSampleId] === 0) {
      this.activeSampleId = undefined;
    }
  }

  private isPointInNewZone(lat: number, lon: number): boolean {
    let out: boolean = false;

    if (!this.activeSampleId) {
      return out;
    }

    for (const szone of cleanSet(flatten(this.mission.getSampleSites().map((site) => site.getSampleZone())))) {
      if (szone?.sample_id !== this.activeSampleId) {
        continue;
      }

      const zone = szone.getZone();

      if (!zone?.contains(lat, lon)) {
        // sample_id is the same but the point is not in the zone.
        // It looks like it is an interleaving case. So, why are we
        // checking it here in a regular zone case?
        out = true;
      }

      break;
    }

    return out;
  }

  private countCoresToAdd(coresMap: Map<SoilCore, boolean>) {
    for (const [core] of coresMap) {
      if (!core.sample_id) {
        throw new Error('Core sample_id not found');
      }

      if (this.coresNumberToAddBySampleId.hasOwnProperty(core.sample_id)) {
        this.coresNumberToAddBySampleId[core.sample_id] += 1;
      } else {
        this.coresNumberToAddBySampleId[core.sample_id] = 1;
      }
    }
  }

  private findClosestCore(
    coresMap: Map<SoilCore, boolean>,
    utmLoc: { easting: number; northing: number; zoneNum: number; zoneLetter: string },
    pointInNewZone: boolean,
  ): SoilCore | null {
    let closestCore = null as SoilCore | null;
    let shortestDistance = -1;

    for (const [core, found] of coresMap) {
      const coreUtmLoc = fromLatLon(core.lat, core.lon);
      const distance = Math.sqrt(
        (coreUtmLoc.easting - utmLoc.easting) ** 2 + (coreUtmLoc.northing - utmLoc.northing) ** 2,
      );

      // If not in middle of a zone, find closest core within 20 meters
      // Do not check if already found here to protect against extra clicks at last core in zone
      // Found check included when adding closest point to new cores
      if (!this.activeSampleId) {
        if (distance < 20 && (shortestDistance < 0 || distance < shortestDistance)) {
          shortestDistance = distance;
          closestCore = core;
        }
        // reorder path has moved to a new zone, reset so error messages only display
        // cores that were missed
      } else if (pointInNewZone) {
        if (distance < 20 && (shortestDistance < 0 || distance < shortestDistance)) {
          this.activeSampleId = core.sample_id;
          shortestDistance = distance;
          closestCore = core;
        }
        // if in middle of a zone, find closest core that is within 20 meters, in the zone,
        // and has not been found yet, helps prevent missing cores near each other
      } else {
        if (
          distance < 20 &&
          (shortestDistance < 0 || distance < shortestDistance) &&
          this.activeSampleId === core.sample_id &&
          !found
        ) {
          shortestDistance = distance;
          closestCore = core;
        }
      }
    }

    return closestCore;
  }

  private async handleGridCase() {
    const samples = cleanSet(flatten(this.mission.getSampleSites().map((site) => site.getSamples()))).sort(
      (s1, s2) => s1.order - s2.order,
    );

    if (samples.length < 2) {
      return samples;
    }

    const originalSampleIndices = samples.map((_: Sample, i: number) => i);

    const selectedSampleIndices: number[] = this.getSelectedSampleIndices(samples);

    const newSampleIndices = this.reorderPathIndices(
      originalSampleIndices,
      selectedSampleIndices,
      originalSampleIndices.reduce((acc, sampleIndex) => {
        const sample = samples[sampleIndex];
        const sampleCentroid = sample.getSampleCentroid();

        if (!sampleCentroid) {
          // Should not happen, but just in case
          acc[sampleIndex] = { lat: 0, lon: 0 };
        } else {
          acc[sampleIndex] = { lat: sampleCentroid.lat, lon: sampleCentroid.lon };
        }

        return acc;
      }, {}),
    );

    const reOrderedSamples: Sample[] = newSampleIndices.map((index: number) => samples[index]);

    for (const [i, sample] of reOrderedSamples.entries()) {
      sample.order = i + 1;
    }

    if (this.renumber) {
      await this.mission.renumber_samples(this.sampling);
    }

    return reOrderedSamples;
  }

  private getSelectedSampleIndices(samples: Sample[]): number[] {
    const selectedSampleIndices: number[] = [];

    for (const [lat, lon] of this.coords) {
      // search for the closest sample point within 20 meters
      const utm_loc = fromLatLon(lat, lon);
      let shortest_distance = -1;
      let sample_index = -1;

      // This code finds the index of the sample that is closest to the given UTM location
      // The code loops through all the samples in the samples array, and calculates the distance between the UTM location and the sample location
      // If the distance is less than the shortest distance found so far, set the shortest distance to the current distance, and set the sample index to the current index
      // The code returns the sample index of the sample that is closest to the given UTM location
      for (const [i, sample] of samples.entries()) {
        const sample_centroid = sample.getSampleSite().getSampleCentroid();

        if (!sample_centroid) {
          throw new Error('Sample centroid not found');
        }

        const sample_utm_loc = fromLatLon(sample_centroid.lat, sample_centroid.lon);
        const distance = Math.sqrt(
          (sample_utm_loc.easting - utm_loc.easting) ** 2 + (sample_utm_loc.northing - utm_loc.northing) ** 2,
        );

        if (distance < REORDER_TOLERANCE_METERS && (shortest_distance < 0 || distance < shortest_distance)) {
          shortest_distance = distance;
          sample_index = i;
        }
      }

      // add the closest point to the new samples
      if (sample_index >= 0) {
        selectedSampleIndices.push(sample_index);
      }
    }

    return selectedSampleIndices;
  }

  reorderPathIndices(
    originalIndices: number[],
    rawSelected: number[],
    indicesLatLons: Record<number, { lat: number; lon: number }>,
  ): number[] {
    const selected = _.uniq(rawSelected);

    if (selected.length < 2) {
      return originalIndices;
    }

    if (selected.length === originalIndices.length) {
      return selected;
    }

    // We are creating a set here to efficiently check for presence of ids, O(1) as opposed to O(n) for array.includes
    const selectedSet = new Set(selected);

    const firstSelected = selected[0];
    const lastSelected = selected[selected.length - 1];
    const direction = lastSelected > firstSelected ? 'forward' : 'backward';

    let out: number[] = [];

    if (direction === 'forward') {
      const prefix = originalIndices.slice(0, firstSelected).filter((id) => !selectedSet.has(id));
      // We are creating a set here to efficiently check for presence of ids, O(1) as opposed to O(n) for array.includes
      const prefixSet = new Set(prefix);

      const lastSelectedPrefix = originalIndices
        .slice(0, lastSelected)
        .filter((id) => !selectedSet.has(id) && !prefixSet.has(id));

      const lastSelectedSuffix = originalIndices.slice(lastSelected + 1).filter((id) => !selectedSet.has(id));

      let suffix: number[] = [];

      if (lastSelectedPrefix[lastSelectedPrefix.length - 1] + 1 === lastSelected) {
        // if we have a sub-path before lastSelected that is attached to lastSelected, reverse it and attach to the path
        suffix = lastSelectedPrefix.reverse();
      } else {
        // otherwise, just take the suffix after lastSelected
        suffix = lastSelectedSuffix;
      }

      out = [
        ...prefix,
        ...selected, // preserve user-defined order
        ...suffix,
      ];
    } else {
      const prefix = originalIndices.slice(0, lastSelected).filter((id) => !selectedSet.has(id));
      // We are creating a set here to efficiently check for presence of ids, O(1) as opposed to O(n) for array.includes
      const prefixSet = new Set(prefix);

      const firstSelectedPrefix = originalIndices
        .slice(0, firstSelected)
        .filter((id) => !selectedSet.has(id) && !prefixSet.has(id));

      const firstSelectedSuffix = originalIndices.slice(firstSelected + 1).filter((id) => !selectedSet.has(id));

      let suffix: number[] = [];

      if (firstSelectedPrefix[firstSelectedPrefix.length - 1] + 1 === firstSelected) {
        // if we have a sub-path before firstSelected that is attached to firstSelected, reverse it and attach to the path
        suffix = firstSelectedPrefix.reverse();
      } else {
        // otherwise, just take the suffix after firstSelected
        suffix = firstSelectedSuffix;
      }

      out = [
        ...prefix,
        ...selected.reverse(), // preserve user-defined order, but reversed
        ...suffix,
      ];
    }

    return this.handleLeftoverIndices(originalIndices, out, indicesLatLons);
  }

  private handleLeftoverIndices(
    originalIndices: number[],
    alreadyReordered: number[],
    indicesLatLons: Record<number, { lat: number; lon: number }>,
  ) {
    // If we have indices that were not included in the reorder,
    // either prepend them to prefix, or append them to suffix.
    const outSet = new Set(alreadyReordered);
    const leftIndices: number[] = originalIndices.filter((id) => !outSet.has(id));

    const firstReorderedIndex = alreadyReordered[0];
    const lastReorderedIndex = alreadyReordered[alreadyReordered.length - 1];
    const firstLeftIndex = leftIndices[0];
    const lastLeftIndex = leftIndices[leftIndices.length - 1];

    if (
      typeof firstReorderedIndex === 'undefined' ||
      typeof lastReorderedIndex === 'undefined' ||
      typeof firstLeftIndex === 'undefined' ||
      typeof lastLeftIndex === 'undefined'
    ) {
      return alreadyReordered;
    }

    const firstReorderedLatLon = indicesLatLons[firstReorderedIndex];
    const lastReorderedLatLon = indicesLatLons[lastReorderedIndex];
    const firstLeftLatLon = indicesLatLons[firstLeftIndex];
    const lastLeftLatLon = indicesLatLons[lastLeftIndex];

    const distanceBetweenFirstReorderedAndFirtLeft = Math.round(
      distanceLatLon(
        firstReorderedLatLon.lat,
        firstReorderedLatLon.lon,
        firstLeftLatLon.lat,
        firstLeftLatLon.lon,
        'Meters',
      ),
    );

    const distanceBetweenFirstReorderedAndLastLeft = Math.round(
      distanceLatLon(
        firstReorderedLatLon.lat,
        firstReorderedLatLon.lon,
        lastLeftLatLon.lat,
        lastLeftLatLon.lon,
        'Meters',
      ),
    );

    const distanceBetweenLastReorderedAndFirstLeft = Math.round(
      distanceLatLon(
        lastReorderedLatLon.lat,
        lastReorderedLatLon.lon,
        firstLeftLatLon.lat,
        firstLeftLatLon.lon,
        'Meters',
      ),
    );

    const distanceBetweenLastReorderedAndLastLeft = Math.round(
      distanceLatLon(
        lastReorderedLatLon.lat,
        lastReorderedLatLon.lon,
        lastLeftLatLon.lat,
        lastLeftLatLon.lon,
        'Meters',
      ),
    );

    const minDistance = Math.min(
      distanceBetweenFirstReorderedAndFirtLeft,
      distanceBetweenFirstReorderedAndLastLeft,
      distanceBetweenLastReorderedAndFirstLeft,
      distanceBetweenLastReorderedAndLastLeft,
    );

    if (minDistance === distanceBetweenFirstReorderedAndFirtLeft) {
      leftIndices.reverse();
      alreadyReordered = [...leftIndices, ...alreadyReordered];
    } else if (minDistance === distanceBetweenFirstReorderedAndLastLeft) {
      alreadyReordered = [...leftIndices, ...alreadyReordered];
    } else if (minDistance === distanceBetweenLastReorderedAndFirstLeft) {
      alreadyReordered = [...alreadyReordered, ...leftIndices];
    } else if (minDistance === distanceBetweenLastReorderedAndLastLeft) {
      leftIndices.reverse();
      alreadyReordered = [...alreadyReordered, ...leftIndices];
    }

    return alreadyReordered;
  }

  reorderPathIndicesPreservingUntouchedPrefixAndSuffix(
    originalIndices: number[],
    indicesToReorder: number[],
  ): number[] {
    const uniqueIndicesToReorder = _.uniq(indicesToReorder);
    const sortedIndicesToReorder = uniqueIndicesToReorder
      .map((id) => originalIndices.indexOf(id))
      .sort((a, b) => a - b);
    const firstIdx = sortedIndicesToReorder[0];
    const lastIdx = sortedIndicesToReorder[sortedIndicesToReorder.length - 1];

    // Break original array into parts
    const prefix = originalIndices.slice(0, firstIdx);
    const middleRaw = originalIndices.slice(firstIdx, lastIdx + 1);
    const suffix = originalIndices.slice(lastIdx + 1);

    const selectedSet = new Set(uniqueIndicesToReorder);
    const remaining = middleRaw.filter((id) => !selectedSet.has(id));

    let reverseRemaining = false;

    if (remaining.length > 0 && uniqueIndicesToReorder.length > 0) {
      const lastSelectedIdx = uniqueIndicesToReorder[uniqueIndicesToReorder.length - 1];

      if (remaining[remaining.length - 1] + 1 === lastSelectedIdx) {
        reverseRemaining = true;
      }
    }

    if (reverseRemaining) {
      remaining.reverse();
    }

    return [
      ...prefix,
      ...uniqueIndicesToReorder, // preserve user-defined order
      ...remaining,
      ...suffix,
    ];
  }
}

export default PathReorderer;
